import React, { useState, useEffect, useRef } from "react";
import { MoreVertical, Plus, ChevronsUpDown, Edit, Trash, Search, Download } from "lucide-react";
import axios from "../api/axios";
import AddTransactionModal from "../components/AddTransactionModal";
import EditTransactionModal from "../components/EditTransactionModal";
import TransactionsToCsv from "../components/TransactionsToCsv";
import MobileTransactionsInfo from "../components/MobileTransactionsInfo"; 

const TransactionsPage = () => {
  const [transactions, setTransactions] = useState([]);
  const [filter, setFilter] = useState("all");
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [searchId, setSearchId] = useState("");
  const [sortByDate, setSortByDate] = useState("unsorted");
  const [sortByAmount, setSortByAmount] = useState("unsorted");
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null); 
  const moreMenuRef = useRef(null);

  useEffect(() => {
    fetchTransactions();
    const handleClickOutside = (event) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target)) {
        setShowMoreMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const fetchTransactions = async () => {
    try {
      const response = await axios.get("/finance/transactions/");
      const transformed = response.data.map((txn) => {
        const dateObj = new Date(txn.date);
        const day = String(dateObj.getDate()).padStart(2, "0");
        const month = dateObj.toLocaleString("default", { month: "long" });
        const year = dateObj.getFullYear();
        const formattedDate = `${day} ${month} ${year}`;

        return {
          ...txn,
          amount: Number(txn.amount),
          date: formattedDate,
          time: dateObj.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        };
      });
      setTransactions(transformed);
    } catch (error) {
      console.error("Failed to fetch transactions:", error);
    }
  };

  const handleAddTransaction = async (newTxn) => {
    try {
      await axios.post("/finance/transactions/", newTxn);
      fetchTransactions();
      setShowAddModal(false);
    } catch (error) {
      console.error("Failed to add transaction:", error);
    }
  };

  const handleEditTransaction = async (updatedTxn) => {
    try {
      await axios.put(`/finance/transactions/${editingTransaction.id}/update/`, updatedTxn);
      fetchTransactions();
      setShowEditModal(false);
      setEditingTransaction(null);
    } catch (error) {
      console.error("Failed to edit transaction:", error);
    }
  };

  const handleDelete = async (transactionId) => {
    try {
      await axios.delete(`/finance/transactions/${transactionId}/`);
      fetchTransactions();
      setOpenMenuIndex(null); 
    } catch (error) {
      console.error("Failed to delete transaction:", error);
    }
  };
  
  const openEditModal = (txn) => {
    setEditingTransaction(txn);
    setShowEditModal(true);
  };

  const filtered = transactions.filter((t) => {
    if (filter !== "all" && t.type !== filter) return false;
    if (searchId && !t.id.toString().includes(searchId)) return false;
    return true;
  });

  const sortTransactions = (transactions, sortByDate, sortByAmount) => {
    let sorted = [...transactions];
    if (sortByDate === "earliestToLatest") {
      sorted.sort((a, b) => new Date(a.date) - new Date(b.date));
    } else if (sortByDate === "latestToEarliest") {
      sorted.sort((a, b) => new Date(b.date) - new Date(a.date));
    }
    if (sortByAmount === "highestToLowest") {
      sorted.sort((a, b) => b.amount - a.amount);
    } else if (sortByAmount === "lowestToHighest") {
      sorted.sort((a, b) => a.amount - b.amount);
    }
    return sorted;
  };

  const sortedTransactions = sortTransactions(filtered, sortByDate, sortByAmount);

  return (
    <div className="min-h-screen bg-white lg:bg-gray-50">
      {/* --- DESKTOP VIEW (lg and up) --- */}
      <div className="hidden lg:block p-6" style={{ backgroundColor: "#f9fafb" }}>
        <h2 className="text-2xl font-bold mb-6 text-gray-800">Manage Transactions</h2>
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-6">
            {["all", "income", "expense"].map((type) => (
              <button
                key={type}
                className={`text-sm font-semibold capitalize transition relative ${
                  filter === type
                    ? "text-[#2ecfe3] after:content-[''] after:absolute after:left-0 after:bottom-[-4px] after:w-full after:h-[2px] after:bg-[#2ecfe3]"
                    : "text-gray-500 hover:text-gray-800"
                }`}
                onClick={() => setFilter(type)}
              >
                {type}
              </button>
            ))}
            <div className="flex items-center border rounded-lg px-3 py-2 bg-white shadow-sm">
              <Search className="h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search ID"
                className="outline-none text-sm ml-3 w-48 bg-transparent"
                value={searchId}
                onChange={(e) => setSearchId(e.target.value)}
              />
            </div>
          </div>
        <div className="flex items-center gap-4">
            <TransactionsToCsv transactions={sortedTransactions} />
<button
  onClick={() => setShowAddModal(true)}
  className="hidden lg:flex items-center gap-2 px-5 py-2.5 
             bg-gradient-to-r from-[#2ecfe3] to-[#25b5c7] 
             text-white font-semibold rounded-xl shadow-md 
             hover:from-[#25b5c7] hover:to-[#1fa3b2] hover:shadow-lg 
             active:scale-[0.98] transition-all duration-200 ease-in-out
             focus:outline-none focus:ring-2 focus:ring-[#2ecfe3] focus:ring-offset-2"
>
  <Plus size={18} className="stroke-[2.5]" />
  Add Transaction
</button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <div className="min-w-[1100px] bg-gray-100 px-8 py-4 border-y rounded-t-lg">
            <div className="grid grid-cols-[1rem_140px_120px_280px_220px_140px_140px_140px] gap-x-20 text-xs font-bold text-gray-600 uppercase text-center">
              <div></div>
              {["Date", "Time", "Transaction ID", "Description", "Amount", "Type", "Action"].map((label, idx) => (
                <div key={idx} className="flex items-center justify-center gap-1">
                  {label}
                  {(label === "Date" || label === "Amount") && (
                    <ChevronsUpDown
                      className="w-4 h-4 text-gray-400 hover:text-[#2ecfe3] cursor-pointer"
                      onClick={() => {
                        if (label === "Date") {
                          setSortByDate(sortByDate === "unsorted" ? "earliestToLatest" : sortByDate === "earliestToLatest" ? "latestToEarliest" : "unsorted");
                        }
                        if (label === "Amount") {
                          setSortByAmount(sortByAmount === "unsorted" ? "highestToLowest" : sortByAmount === "highestToLowest" ? "lowestToHighest" : "unsorted");
                        }
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="min-w-[1100px]">
            {sortedTransactions.map((txn) => (
              <div
                key={txn.id}
                className="grid grid-cols-[1rem_140px_120px_280px_220px_140px_140px_140px] gap-x-20 items-center bg-white px-8 py-4 text-sm text-center border-b"
              >
                <div className="flex justify-center">
                  <span className={`block w-3 h-3 rounded-full ${txn.type === "income" ? "bg-green-500" : "bg-red-500"}`}></span>
                </div>
                <div>{txn.date}</div>
                <div>{txn.time}</div>
                <div className="truncate">{txn.id}</div>
                <div className="truncate">{txn.description}</div>
                <div className="font-mono font-medium text-center">€{txn.amount.toFixed(2)}</div>
                <div>
                  <span className={`text-sm font-semibold ${txn.type === "income" ? "text-green-600" : "text-red-600"}`}>
                    {txn.type.charAt(0).toUpperCase() + txn.type.slice(1)}
                  </span>
                </div>
                <div className="flex justify-center items-center gap-2">
                  <button onClick={() => openEditModal(txn)}>
                    <Edit className="w-5 h-5 text-gray-600 hover:text-blue-600" />
                  </button>
                  <button onClick={() => handleDelete(txn.id)}>
                    <Trash className="w-5 h-5 text-gray-600 hover:text-red-600" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* --- MOBILE VIEW (up to lg) --- */}
      <div className="block lg:hidden p-4 pb-24">
        <h2 className="text-xl font-bold mb-4 text-gray-800">Manage Transactions</h2>
        <div className="flex justify-center items-center bg-gray-200 rounded-lg p-1 mb-4">
          {["all", "income", "expense"].map((type) => (
            <button
              key={type}
              className={`flex-1 py-2 text-sm font-semibold capitalize transition rounded-md ${
                filter === type ? "bg-white text-[#2ecfe3] shadow" : "text-gray-600"
              }`}
              onClick={() => setFilter(type)}
            >
              {type}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 mb-4">
          <div className="flex-grow flex items-center border rounded-lg px-3 py-2 bg-white shadow-sm">
            <Search className="h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by ID"
              className="outline-none text-sm ml-2 w-full bg-transparent"
              value={searchId}
              onChange={(e) => setSearchId(e.target.value)}
            />
          </div>
          <div className="relative" ref={moreMenuRef}>
            <button
              onClick={() => setShowMoreMenu(!showMoreMenu)}
              className="p-2.5 border rounded-lg bg-white shadow-sm"
            >
              <MoreVertical size={20} className="text-gray-600" />
            </button>
            {showMoreMenu && (
              <div className="absolute right-0 mt-2 w-52 bg-white rounded-lg shadow-xl z-20 p-2 border">
                <TransactionsToCsv 
                  transactions={sortedTransactions} 
                  isMobile={true} 
                  onExport={() => setShowMoreMenu(false)}
                />
              </div>
            )}
          </div>
        </div>
        <div className="space-y-3">
          {sortedTransactions.map((txn) => (
            <div 
              key={txn.id} 
              className="bg-white rounded-lg shadow-md p-4 border border-gray-200 cursor-pointer active:scale-[0.98] transition-transform duration-150 ease-in-out"
              onClick={() => setSelectedTransaction(txn)}
            >
              <div className="flex justify-between items-start">
                <div className="flex-grow">
                  <p className="font-bold text-gray-800 truncate pr-2">{txn.description || "No Description"}</p>
                  <p className="text-sm text-gray-500">{txn.date}</p>
                </div>
                <p className={`font-semibold text-lg ${txn.type === "income" ? "text-green-600" : "text-red-600"}`}>
                  {txn.type === "income" ? "+" : "-"}€{txn.amount.toFixed(2)}
                </p>
              </div>
            </div>
          ))}
          {sortedTransactions.length === 0 && (
            <div className="text-center py-10">
              <p className="text-gray-500">No transactions found.</p>
            </div>
          )}
        </div>
      </div>

      {/* --- FLOATING ACTION BUTTON (Mobile) --- */}
      <button
        onClick={() => setShowAddModal(true)}
        className="lg:hidden fixed bottom-6 right-6 w-14 h-14 bg-[#2ecfe3] text-white rounded-full flex items-center justify-center shadow-lg hover:bg-[#24b9cc] transition z-10"
      >
        <Plus size={28} />
      </button>

      {/* --- MODALS (reused for both views) --- */}
      <AddTransactionModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleAddTransaction}
      />
      {showEditModal && editingTransaction && (
        <EditTransactionModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          transaction={editingTransaction}
          onSubmit={handleEditTransaction}
        />
      )}

      <MobileTransactionsInfo 
        transaction={selectedTransaction}
        onClose={() => setSelectedTransaction(null)}
        onEdit={openEditModal}
        onDelete={handleDelete}
      />
    </div>
  );
};

export default TransactionsPage;